// src/components/payroll/SplitPayrollModal.js

import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { Send, Save, CheckCircle, RotateCcw, Trash2 } from 'lucide-react';

// --- Helpers ---
const parseHoursStringToDecimal = (hourString) => {
    if (!hourString || typeof hourString !== 'string') return 0;
    const hoursMatch = hourString.match(/(\d+)h/);
    const minutesMatch = hourString.match(/(\d+)m/);
    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    return hours + minutes / 60;
};

const formatDate = (dateString) => {
    if(!dateString) return '';
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
    });
};

const SplitPayrollModal = ({ onClose, summaryData, propertyId, startDate, endDate, periodType }) => {
    const [employees, setEmployees] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    const [existingStatementId, setExistingStatementId] = useState(null);
    const [statementStatus, setStatementStatus] = useState(null);
    const [pendingAction, setPendingAction] = useState(null); 

    useEffect(() => {
        const loadAndPrepareData = async () => {
            setIsLoading(true);
            try {
                const record = await api.getPaymentStatement({ propertyId, startDate, endDate, periodType });

                let draftMap = new Map();
                if (record) {
                    setExistingStatementId(record.id);
                    setStatementStatus(record.status);
                    draftMap = new Map(record.lines.map(line => [line.employee_user_id, line]));
                    if (record.status !== 'APPROVED') {
                        toast.success('Draft loaded.');
                    }
                } else {
                    setExistingStatementId(null);
                    setStatementStatus(null);
                }

                const mergedEmployees = Object.values(summaryData).map(summary => {
                    const draftLine = draftMap.get(summary.employee_user_id);
                    return {
                        employeeUserId: summary.employee_user_id,
                        name: summary.employee_name,
                        originalHours: parseHoursStringToDecimal(summary.total_hours),
                        payrollHours: draftLine ? parseFloat(draftLine.payroll_hours).toString() : '', 
                        notes: draftLine ? draftLine.notes : ''
                    };
                });
                setEmployees(mergedEmployees);

            } catch (error) {
                console.error("Error loading payroll data:", error);
                toast.error("Could not load payroll data.");
            } finally {
                setIsLoading(false);
            }
        };

        if (propertyId && startDate && endDate && summaryData) {
            loadAndPrepareData();
        } else {
            setIsLoading(false);
        }
    }, [summaryData, propertyId, startDate, endDate, periodType]);


    const handleHoursChange = (index, value) => {
        const updatedEmployees = [...employees];
        
        // 1. Allow clearing
        if (value === '') {
            updatedEmployees[index].payrollHours = '';
            setEmployees(updatedEmployees);
            return;
        }

        const numericValue = parseFloat(value);
        // Ensure accurate max limit comparison
        const maxLimit = Number(updatedEmployees[index].originalHours); 

        // 2. Prevent Negative
        if (numericValue < 0) return;

        // 3. Prevent Exceeding Original Hours
        // We allow the state update ONLY if it is valid.
        if (numericValue > maxLimit) {
            toast.error(`Max limit is ${maxLimit.toFixed(2)} hours.`);
            return; // Block the update
        }

        updatedEmployees[index].payrollHours = value;
        setEmployees(updatedEmployees);
    };

    const handleNotesChange = (index, value) => {
        const updatedEmployees = [...employees];
        updatedEmployees[index].notes = value;
        setEmployees(updatedEmployees);
    };

    const executeSubmission = async (submissionType, listOverride = null) => {
        setIsSubmitting(true);

        const currentList = listOverride || employees;

        const linesToSave = currentList.filter(emp => {
            if (emp.payrollHours === '' || emp.payrollHours === null) return false;
            
            const val = parseFloat(emp.payrollHours);
            const max = parseFloat(emp.originalHours);

            // Validation: Must be >= 1 AND <= Original
            if (isNaN(val) || val < 1) return false;
            if (val > max) return false; // Extra safety filter
            
            return true; 
        });

        const payload = {
            propertyId,
            startDate,
            endDate,
            submissionType,
            employees: linesToSave,
            periodType: periodType || 'CUSTOM'
        };

        try {
            const res = await api.submitPaymentStatement(payload);
            
            if (res.statementId) {
                setExistingStatementId(res.statementId);
            }

            if (listOverride) {
                if (linesToSave.length === 0) setExistingStatementId(null);
                toast.success("Record removed.");
            } else {
                toast.success(res.message);
                onClose(); 
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to process statement");
            setPendingAction(null); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteRow = async (index) => {
        const updatedEmployees = [...employees];
        updatedEmployees[index].payrollHours = '';
        updatedEmployees[index].notes = '';
        setEmployees(updatedEmployees);

        if (existingStatementId) {
            await executeSubmission('DRAFT', updatedEmployees);
        }
    };

    const totals = useMemo(() => {
        return employees.reduce((acc, emp) => {
            acc.original += emp.originalHours;
            const pVal = parseFloat(emp.payrollHours);
            acc.payroll += (!isNaN(pVal) && pVal >= 0) ? pVal : 0;
            return acc;
        }, { original: 0, payroll: 0 });
    }, [employees]);

    const hasAnyValidData = useMemo(() => {
        return employees.some(emp => {
            const val = parseFloat(emp.payrollHours);
            const max = parseFloat(emp.originalHours);
            return !isNaN(val) && val >= 1 && val <= max;
        });
    }, [employees]);

    // Check for invalid inputs to disable button (Visual Feedback)
    const hasInvalidInputs = useMemo(() => {
        return employees.some(emp => {
            if (emp.payrollHours === '' || emp.payrollHours === null) return false;
            const val = parseFloat(emp.payrollHours);
            const max = parseFloat(emp.originalHours);
            // Invalid if: Less than 1 OR Greater than Max
            return (!isNaN(val) && val < 1) || (!isNaN(val) && val > max);
        });
    }, [employees]);

    const initiateAction = (type) => {
        if (type === 'SENT') {
            setPendingAction({
                type: 'SENT',
                title: 'Send to Accountant?',
                description: 'This will submit the final hours and email the report to the accountant.',
                color: '#0284c7', 
                btnText: 'Yes, Send Email',
                icon: Send,
                btnClass: 'button action-button'
            });
        } else if (type === 'APPROVED') {
            setPendingAction({
                type: 'APPROVED',
                title: 'Approve & Lock?',
                description: 'This locks the statement preventing further edits.',
                color: '#16a34a', 
                btnText: 'Yes, Approve',
                icon: CheckCircle,
                btnClass: 'button action-button-approve'
            });
        } else if (type === 'DRAFT' && statementStatus === 'APPROVED') {
            setPendingAction({
                type: 'DRAFT',
                title: 'Un-approve Statement?',
                description: 'Revert to Draft and unlock for editing?',
                color: '#d97706', 
                btnText: 'Yes, Un-approve',
                icon: RotateCcw,
                btnClass: 'button action-button-unapprove'
            });
        } else {
            executeSubmission('DRAFT');
        }
    };

    if (isLoading) { return <div className="modal-overlay"><div className="modal-content"><p>Loading...</p></div></div>; }

    const isLocked = statementStatus === 'APPROVED';

    let saveButtonTitle = "";
    if (hasInvalidInputs) saveButtonTitle = "Fix invalid hours (Check limits)";
    else if (!hasAnyValidData) saveButtonTitle = "Enter valid hours to save";

    return (
        <div className="modal-overlay">
            {pendingAction ? (
                <div className="modal-content" style={{ maxWidth: '450px', textAlign: 'center', padding: '30px 25px' }}>
                    <div style={{ margin: '0 auto 20px', width: '60px', height: '60px', borderRadius: '50%', backgroundColor: `${pendingAction.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: pendingAction.color }}>
                        <pendingAction.icon size={32} />
                    </div>
                    <h2 style={{ margin: '0 0 10px', fontSize: '1.5rem', color: '#1e293b' }}>{pendingAction.title}</h2>
                    <p style={{ color: '#64748b', marginBottom: '30px', lineHeight: '1.5' }}>{pendingAction.description}</p>
                    <div className="confirm-actions">
                        <button className="button button-secondary" onClick={() => setPendingAction(null)} disabled={isSubmitting}>Cancel</button>
                        <button className={pendingAction.btnClass} onClick={() => executeSubmission(pendingAction.type)} disabled={isSubmitting}>{isSubmitting ? 'Processing...' : pendingAction.btnText}</button>
                    </div>
                </div>
            ) : (
                <div className="modal-content large">
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <h2>Prepare Payroll Statement</h2>
                        {isLocked && <span style={{background:'#dcfce7', color:'#166534', padding:'4px 12px', borderRadius:'15px', fontSize:'0.85rem', fontWeight:'bold', border:'1px solid #86efac'}}>Approved & Locked</span>}
                    </div>
                    
                    <p className="modal-subtitle">
                        Showing data for period: <strong>{formatDate(startDate)}</strong> to <strong>{formatDate(endDate)}</strong>
                    </p>
                    
                    <form onSubmit={(e) => e.preventDefault()}>
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Employee Name</th>
                                        <th>Original Hours</th>
                                        <th>Payroll Hours {isLocked ? '(Locked)' : '(Editable)'}</th>
                                        <th>Notes</th>
                                        {!isLocked && <th style={{width: '60px', textAlign:'center'}}>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.map((emp, index) => {
                                        const hasData = (emp.payrollHours !== '' && emp.payrollHours !== null) || (emp.notes && emp.notes.trim() !== '');
                                        
                                        const pVal = parseFloat(emp.payrollHours);
                                        const max = parseFloat(emp.originalHours);
                                        const isInvalid = emp.payrollHours !== '' && (pVal < 1 || pVal > max);

                                        return (
                                            <tr key={emp.employeeUserId}>
                                                <td>{emp.name}</td>
                                                <td>{emp.originalHours.toFixed(2)}</td>
                                                <td>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        min="1"
                                                        max={emp.originalHours} // HTML5 max attribute
                                                        className="form-input table-input" 
                                                        value={emp.payrollHours} 
                                                        onChange={(e) => handleHoursChange(index, e.target.value)} 
                                                        placeholder="-"
                                                        disabled={isLocked} 
                                                        style={{
                                                            ...(isLocked ? {backgroundColor: '#f9fafb', cursor: 'not-allowed'} : {}),
                                                            ...(isInvalid ? {borderColor: '#ef4444', backgroundColor: '#fef2f2'} : {})
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <input 
                                                        type="text" 
                                                        className="form-input table-input" 
                                                        value={emp.notes} 
                                                        onChange={(e) => handleNotesChange(index, e.target.value)} 
                                                        placeholder={isLocked ? "" : "e.g., 3 hours through payroll"}
                                                        disabled={isLocked} 
                                                        style={isLocked ? {backgroundColor: '#f9fafb', cursor: 'not-allowed'} : {}}
                                                    />
                                                </td>
                                                {!isLocked && (
                                                    <td style={{textAlign:'center'}}>
                                                        <button 
                                                            type="button" 
                                                            className="icon-button delete-button" 
                                                            onClick={() => hasData && handleDeleteRow(index)}
                                                            disabled={!hasData} 
                                                            title={hasData ? "Clear and remove" : "No data"}
                                                            style={{
                                                                color: hasData ? '#ef4444' : '#cbd5e1', 
                                                                cursor: hasData ? 'pointer' : 'not-allowed',
                                                                opacity: hasData ? 1 : 0.5
                                                            }}
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th>Hours</th>
                                        <td>{totals.original.toFixed(2)}</td>
                                        <td>{totals.payroll.toFixed(2)}</td>
                                        <td></td>
                                        {!isLocked && <td></td>}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="modal-actions" style={{ gap: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="button" className="button button-secondary" onClick={onClose} disabled={isSubmitting}>Close</button>
                            
                            {isLocked ? (
                                <button type="button" className="button action-button-unapprove" onClick={() => initiateAction('DRAFT')} disabled={isSubmitting}>
                                    <RotateCcw size={18} /> {isSubmitting ? 'Processing...' : 'Un-approve (Unlock)'}
                                </button>
                            ) : (
                                <>
                                    <button 
                                        type="button" 
                                        className="button action-button-draft"
                                        onClick={() => initiateAction('DRAFT')} 
                                        disabled={isSubmitting || !hasAnyValidData || hasInvalidInputs}
                                        title={saveButtonTitle}
                                    >
                                        <Save size={18} /> {isSubmitting ? '...' : 'Save Draft'}
                                    </button>

                                    <button 
                                        type="button" 
                                        className="button action-button-approve"
                                        onClick={() => initiateAction('APPROVED')} 
                                        disabled={isSubmitting || !existingStatementId || hasInvalidInputs} 
                                        title={!existingStatementId ? "Save a Draft first" : saveButtonTitle}
                                    >
                                        <CheckCircle size={18} /> {isSubmitting ? '...' : 'Approve'}
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default SplitPayrollModal;